import React from 'react';

import Text from '@lightspeed/cirrus-text';
import cn from 'classnames';
import PropTypes from 'prop-types';

const inputClass = 'cr-textarea';
const containerClass = 'cr-textarea__container';
const wrapperClass = 'cr-textarea__wrapper';

const Textarea = ({
  id,
  label,
  labelHelper,
  description,
  textHelper,
  disabled,
  readOnly,
  status,
  rows,
  ...rest
}) => {
  const wrapperClasses = cn(wrapperClass, {
    [`${wrapperClass}--disabled`]: disabled,
    [`${wrapperClass}--readonly`]: readOnly,
  });

  const inputClasses = cn(inputClass, {
    [`${inputClass}--${status && status.type}`]: status && status.type,
  });

  const labelWrapperMarkup = label ? (
    <div className="cr-textarea__label-wrapper">
      <Text element="label" bodySmall bold htmlFor={id} id={id ? cn(label && `${id}-label`) : null}>
        {label}
      </Text>
      {labelHelper ? <div className="cr-textarea__label-helper">{labelHelper}</div> : null}
    </div>
  ) : null;

  const descriptionMarkup = description ? (
    <Text element="div" bodySmall id={`${id}-description`}>
      {description}
    </Text>
  ) : null;

  const labelHeaderMarkup =
    label || description ? (
      <div className="cr-textarea__header">
        {labelWrapperMarkup}
        {descriptionMarkup}
      </div>
    ) : null;

  const labelledBy = id ? cn(label && `${id}-label`) : null;

  const describedBy = id ? cn(description && `${id}-description`) : null;

  const textareaMarkup = (
    <textarea
      id={id}
      rows={rows}
      {...rest}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className={inputClasses}
      disabled={disabled}
      readOnly={readOnly}
    />
  );

  const statusHelperClass = cn('cr-textarea__text-helper', {
    [`cr-textarea__text-helper--${status && status.type}`]: status && status.type,
  });

  const statusHelperMarkup =
    status && status.message ? (
      <Text element="div" bodySmall className={statusHelperClass}>
        {status.message}
      </Text>
    ) : null;

  const textHelperMarkup = textHelper ? (
    <Text element="div" bodySmall dimmed className="cr-textarea__text-helper">
      {textHelper}
    </Text>
  ) : null;

  const backdropMarkup = <div className="cr-textarea__backdrop" />;

  return (
    <div className={containerClass}>
      {labelHeaderMarkup}
      <label htmlFor={id} className={wrapperClasses}>
        {textareaMarkup}
        {backdropMarkup}
      </label>
      {statusHelperMarkup || textHelperMarkup}
    </div>
  );
};

Textarea.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  labelHelper: PropTypes.node,
  description: PropTypes.string,
  textHelper: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  status: PropTypes.shape({
    type: PropTypes.oneOf(['valid', 'warning', 'error']),
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  }),
  rows: PropTypes.number,
};

Textarea.defaultProps = {
  rows: 4,
};

export default Textarea;
